import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import UploadIcon from "../../Images/upload-cloud-01.svg";
import PlusIcon from "../../Images/PlusIcon.svg";
import ToastMessage from "../../config/ToastMessage";
// import { uploadDocumentToCloudinary } from "../../config/cloudinary";
import { API_URL } from "../../config/api";
import pdfIcon from "../../Images/pdf_icon.svg";

export default function AssignmentCreate({
  isOpen,
  onClose,
  selectedLesson,
  assignmentData: editData = null,
  publisherFormData = null, // ✅ Receive publisher form data
  onSave,
}) {
  const [assignmentData, setAssignmentData] = useState({
    title: "",
    summary: "",
    maxPoints: "",
    minPassPoints: "",
    maxFileSize: "",
    fileSupport: "PDF",
    attachment: null,
  });

  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [toast, setToast] = useState({ type: "", message: "" });

  // ============================
  //  ✅ PREFILL FORM (EDIT MODE OR NEW WITH PUBLISHER DATA)
  // ============================
  useEffect(() => {
    // If editing existing assignment
    if (editData) {
      setAssignmentData({
        title: editData.title || "",
        summary: editData.instructions || "",
        maxPoints: editData.max_points?.toString() || "",
        minPassPoints: editData.min_points?.toString() || "",
        maxFileSize: editData.max_file_size_mb?.toString() || "",
        fileSupport: editData.file_supported_types || "PDF",
        attachment: null,
      });
    }
    // If creating new assignment with publisher form data
    else if (publisherFormData) {
      setAssignmentData((prev) => ({
        ...prev,
        batch: publisherFormData.batch || "",
      }));
    }
  }, [editData, publisherFormData]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setAssignmentData({ ...assignmentData, attachment: file });
  };

  // Azure upload handler
  const handleFileUpload = async (file) => {
    if (!file) return null;

    setLoading(true);
    setToast({ type: "", message: "" });

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API_URL}/azure-upload/pdf-upload`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Upload failed");
      }

      const data = await response.json();

      setToast({
        type: "success",
        message: "File uploaded successfully!",
      });

      // ✅ Store these in DB
      return data.url;

    } catch (error) {
      console.error("Upload error:", error);

      setToast({
        type: "error",
        message: error.message || "Upload failed. Please try again.",
      });

      return null;
    } finally {
      setLoading(false);
    }
  };

  // ============================
  //  ✅ VALIDATE FORM
  // ============================
  const validateForm = () => {
    const errors = {};

    if (!assignmentData.title.trim()) {
      errors.title = "Assignment title is required";
    }

    if (!assignmentData.summary.trim()) {
      errors.summary = "Summary is required";
    }

    if (!assignmentData.maxPoints || parseInt(assignmentData.maxPoints) <= 0) {
      errors.max_points = "Maximum points must be greater than 0";
    }

    if (
      !assignmentData.minPassPoints ||
      parseInt(assignmentData.minPassPoints) < 0
    ) {
      errors.min_pass_points = "Minimum pass points is required";
    }

    if (
      parseInt(assignmentData.minPassPoints) >
      parseInt(assignmentData.maxPoints)
    ) {
      errors.min_pass_points =
        "Minimum pass points cannot exceed maximum points";
    }

    if (
      !assignmentData.maxFileSize ||
      parseInt(assignmentData.maxFileSize) <= 0
    ) {
      errors.max_file_size_mb = "File size limit is required";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ============================
  //  ✅ CREATE / UPDATE ASSIGNMENT
  // ============================
  const handlePublish = async () => {
    // Validate form
    if (!validateForm()) {
      setToast({
        type: "error",
        message: "Please fill in all required fields correctly",
      });
      return;
    }

    setLoading(true);

    try {
      let fileUrl = editData?.file_url || null;

      // Upload file if new file is selected
      if (assignmentData.attachment) {
        fileUrl = await handleFileUpload(assignmentData.attachment);
        if (!fileUrl) {
          setLoading(false);
          return;
        }
      }

      // ✅ Prepare assignment payload matching backend schema
      const payload = {
        lesson_id: selectedLesson.lesson_id || selectedLesson.lessonDbId,
        title: assignmentData.title,
        instructions: assignmentData.summary,
        min_points: parseInt(assignmentData.minPassPoints) || 0,
        max_points: parseInt(assignmentData.maxPoints) || 100,
        file_url: fileUrl || "",
        max_file_size_mb: parseInt(assignmentData.maxFileSize) || 10,
        file_supported_types: assignmentData.fileSupport,
      };

      const url = editData
        ? `${API_URL}/assignments/${editData.assignment_id}`
        : `${API_URL}/assignments/`;

      const method = editData ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok || data.success) {
        setToast({
          type: "success",
          message:
            data.message ||
            `Assignment ${editData ? "updated" : "created"} successfully!`,
        });

        // Wait a bit for toast to show, then call onSave and close
        setTimeout(() => {
          if (onSave) onSave(data.data || data);
          handleClose();
        }, 1000);
      } else {
        // Handle different error formats
        let errorMessage = "Failed to save assignment";

        if (data.message) {
          errorMessage = data.message;
        } else if (data.detail) {
          // Handle Pydantic validation errors (array of error objects)
          if (Array.isArray(data.detail)) {
            errorMessage = data.detail
              .map((err) => err.msg || JSON.stringify(err))
              .join(", ");
          } else if (typeof data.detail === "string") {
            errorMessage = data.detail;
          } else if (typeof data.detail === "object") {
            // Handle other object formats
            errorMessage = JSON.stringify(data.detail);
          }
        }

        setToast({
          type: "error",
          message: errorMessage,
        });
      }
    } catch (err) {
      console.error("Error saving assignment:", err);
      let errorMessage = "An error occurred while saving";

      if (err.message) {
        errorMessage = err.message;
      } else if (typeof err === "object") {
        errorMessage = JSON.stringify(err);
      }

      setToast({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAssignmentData({
      title: "",
      summary: "",
      maxPoints: "",
      minPassPoints: "",
      maxFileSize: "",
      fileSupport: "PDF",
      attachment: null,
    });
    setFieldErrors({});
    setToast({ type: "", message: "" });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[1181px] max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-9 py-5 border-b sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-900">
            {editData ? "Edit Assignment" : "Create Assignment"}
          </h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-9 py-6">
          {/* Course Info Box */}
          <div className="bg-white rounded shadow-[0px_4px_8px_1px_rgba(34,40,30,0.07)] border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-2 gap-x-32 gap-y-8">
              <div>
                <div className="text-stone-900 text-base font-normal mb-2">
                  Program
                </div>
                <div className="text-zinc-800 text-lg font-semibold">
                  {selectedLesson?.program || selectedLesson?.courseTitle || ""}
                </div>
              </div>

              <div>
                <div className="text-stone-900 text-base font-normal mb-2">
                  Lesson {selectedLesson?.lessonDisplayId || "1"}
                </div>
                <div className="text-zinc-800 text-lg font-semibold">
                  {selectedLesson?.lesson || selectedLesson?.lessonTitle || " "}
                </div>
              </div>

              <div>
                <div className="text-stone-900 text-base font-normal mb-2">
                  Module {selectedLesson?.moduleDisplayId || "1"}
                </div>
                <div className="text-zinc-800 text-lg font-semibold">
                  {selectedLesson?.module || selectedLesson?.moduleTitle || " "}
                </div>
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-6">
            {/* Row 1: Assignment Title, Maximum Points, Minimum Pass Points */}
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-6">
                <label className="block text-zinc-800 text-sm font-bold mb-2">
                  Assignment Title
                </label>
                <input
                  type="text"
                  value={assignmentData.title}
                  onChange={(e) =>
                    setAssignmentData({
                      ...assignmentData,
                      title: e.target.value,
                    })
                  }
                  placeholder="Enter assignment title"
                  className={`w-full px-3 py-2.5 bg-neutral-50 rounded border border-stone-300 text-sm placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#4AACC5] ${
                    fieldErrors.title ? "border-red-500" : ""
                  }`}
                />
                {fieldErrors.title && (
                  <p className="text-red-600 text-sm mt-1">
                    {fieldErrors.title}
                  </p>
                )}
              </div>

              <div className="col-span-3">
                <label className="block text-zinc-800 text-sm font-bold mb-2">
                  Maximum Points
                </label>
                <input
                  type="text"
                  value={assignmentData.maxPoints}
                  onChange={(e) =>
                    setAssignmentData({
                      ...assignmentData,
                      maxPoints: e.target.value,
                    })
                  }
                  placeholder="Maximum a student can score"
                  className={`w-full px-3 py-2.5 bg-neutral-50 rounded border border-stone-300 text-sm placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#4AACC5] ${
                    fieldErrors.max_points ? "border-red-500" : ""
                  }`}
                />
                {fieldErrors.max_points && (
                  <p className="text-red-600 text-sm mt-1">
                    {fieldErrors.max_points}
                  </p>
                )}
              </div>

              <div className="col-span-3">
                <label className="block text-zinc-800 text-sm font-bold mb-2">
                  Minimum Pass Points
                </label>
                <input
                  type="text"
                  value={assignmentData.minPassPoints}
                  onChange={(e) =>
                    setAssignmentData({
                      ...assignmentData,
                      minPassPoints: e.target.value,
                    })
                  }
                  placeholder="Enter Minimum Pass Points"
                  className={`w-full px-3 py-2.5 bg-neutral-50 rounded border border-stone-300 text-sm placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#4AACC5] ${
                    fieldErrors.min_pass_points ? "border-red-500" : ""
                  }`}
                />
                {fieldErrors.min_pass_points && (
                  <p className="text-red-600 text-sm mt-1">
                    {fieldErrors.min_pass_points}
                  </p>
                )}
              </div>
            </div>

            {/* Row 2: Summary (left) and Attachment (right) */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-zinc-800 text-sm font-bold mb-2">
                  Summary
                </label>
                <textarea
                  value={assignmentData.summary}
                  onChange={(e) =>
                    setAssignmentData({
                      ...assignmentData,
                      summary: e.target.value,
                    })
                  }
                  placeholder="Write Text"
                  rows="5"
                  className={`w-full px-3 py-2.5 bg-neutral-50 rounded border border-stone-300 text-sm placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#4AACC5] resize-none ${
                    fieldErrors.summary ? "border-red-500" : ""
                  }`}
                />
                {fieldErrors.summary && (
                  <p className="text-red-600 text-sm mt-1">
                    {fieldErrors.summary}
                  </p>
                )}

                {/* Row 3: Maximum File Size Limit and File Support - Under Summary */}
                <div className="grid grid-cols-2 gap-6 mt-4">
                  <div>
                    <label className="block text-zinc-800 text-sm font-bold mb-2">
                      Maximum File Size Limit
                    </label>
                    <input
                      type="text"
                      value={assignmentData.maxFileSize}
                      onChange={(e) =>
                        setAssignmentData({
                          ...assignmentData,
                          maxFileSize: e.target.value,
                        })
                      }
                      placeholder="MB"
                      className={`w-full px-3 py-2.5 bg-neutral-50 rounded border border-stone-300 text-sm placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#4AACC5] ${
                        fieldErrors.max_file_size_mb ? "border-red-500" : ""
                      }`}
                    />
                    {fieldErrors.max_file_size_mb && (
                      <p className="text-red-600 text-sm mt-1">
                        {fieldErrors.max_file_size_mb}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-zinc-800 text-sm font-bold mb-2">
                      File Support
                    </label>
                    <select
                      value={assignmentData.fileSupport}
                      onChange={(e) =>
                        setAssignmentData({
                          ...assignmentData,
                          fileSupport: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2.5 bg-neutral-50 rounded border border-stone-300 text-sm text-zinc-700 focus:outline-none focus:ring-2 focus:ring-[#4AACC5]"
                    >
                      <option>PDF</option>
                      <option>DOC</option>
                      <option>DOCX</option>
                      <option>All Formats</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-zinc-800 text-sm font-bold mb-2">
                  Attachment
                </label>
                <label className="h-[128px] px-3 py-2 bg-neutral-50 rounded-lg border border-stone-300 flex flex-col justify-center items-center gap-2 cursor-pointer hover:bg-gray-100 transition">
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx"
                  />
                  <img src={UploadIcon} alt="Upload" className="w-10 h-10" />
                  <div className="text-neutral-500 text-sm text-center">
                    Upload Assignment Materials
                  </div>
                </label>

                {/* File Preview */}
                {assignmentData.attachment && (
                  <div className="mt-3 p-3 bg-red-50 rounded border border-red-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center">
                        <span className="text-red-600 text-xs font-bold">
                          PDF
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-zinc-800">
                          Assignment
                        </div>
                        <div className="text-xs text-zinc-500">
                          {assignmentData.attachment.name}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() =>
                        setAssignmentData({
                          ...assignmentData,
                          attachment: null,
                        })
                      }
                      className="text-zinc-400 hover:text-zinc-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {editData?.file_url && !assignmentData.attachment && (
                  <>
                    <div className="mt-3 p-3 bg-neutral-50 rounded border border-stone-300 flex items-center gap-3 w-52">
                      <img src={pdfIcon} className="w-10 h-10" alt="PDF" />
                      <div className="text-xs break-all">
                        {getFileNameFromUrl(editData.file_url) ||
                          editData.file_url.split("/").pop()}
                      </div>
                    </div>

                    <a
                      href={editData.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#4AACC5] underline mt-2 inline-block"
                    >
                      View Reference File
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mt-8">
            <button
              onClick={handleClose}
              className="w-[140px] h-11 px-6 py-2.5 rounded border border-stone-300 flex justify-center items-center text-zinc-800 text-base font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handlePublish}
              disabled={loading}
              className="w-[140px] h-11 px-6 py-2.5 bg-[#4AACC5] rounded flex justify-center items-center text-white text-base font-medium hover:bg-[#3d99b0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Saving..." : editData ? "Update" : "Submit"}
            </button>
          </div>
        </div>
      </div>

      <ToastMessage
        type={toast.type}
        message={toast.message}
        onClose={() => setToast({ type: "", message: "" })}
      />
    </div>
  );
}

const getFileNameFromUrl = (url) => {
  try {
    const raw = url.split("/").pop().split("?")[0];
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
};
